import { STATES_AUSTRALIA } from '@admiin-com/ds-common';
import {
  AccountStatus,
  Contact,
  Entity,
  FromToType,
} from '@admiin-com/ds-graphql';

export function isVerifiedEntity(entity: Entity) {
  return entity?.accountStatus === AccountStatus.ACTIVE;
}

export function isVerifiedBeneficialOwner(entity: Entity) {
  // for()
}

export function isIndividualEntity(business: Entity | null | undefined) {
  return false;
  // business?.type === EntityType.INDIVIDUAL ||
  // business?.type === EntityType.SOLE_TRADER
}

export function isEntityOrContact(business: Entity | Contact) {
  if (!business) return null;
  if ('entityId' in business) return FromToType.CONTACT;
  else return FromToType.ENTITY;
}

export function isNormalContact(contact: Entity | Contact) {
  console.log(contact);
  return (
    isEntityOrContact(contact) === FromToType.CONTACT &&
    !(contact as any)?.metadata
  );
}

export const mapStateCode = (state: string): string => {
  const stateCode = STATES_AUSTRALIA.find((s) => s.name === state)?.code;
  return stateCode ?? '';
};

// Map country names to ISO 3166-1 alpha-2 country codes
export const COUNTRY_CODE_MAP: Record<string, string> = {
  Australia: 'AU',
  'United States': 'US',
  'United Kingdom': 'GB',
  Canada: 'CA',
  'New Zealand': 'NZ',
  Singapore: 'SG',
  India: 'IN',
  China: 'CN',
  Japan: 'JP',
  Germany: 'DE',
  France: 'FR',
  Italy: 'IT',
  Spain: 'ES',
  Netherlands: 'NL',
  Belgium: 'BE',
  Switzerland: 'CH',
  Austria: 'AT',
  Sweden: 'SE',
  Norway: 'NO',
  Denmark: 'DK',
  Finland: 'FI',
  Ireland: 'IE',
  Portugal: 'PT',
  Greece: 'GR',
  Poland: 'PL',
  'Czech Republic': 'CZ',
  Hungary: 'HU',
  Romania: 'RO',
  Bulgaria: 'BG',
  Croatia: 'HR',
  Slovakia: 'SK',
  Slovenia: 'SI',
  Lithuania: 'LT',
  Latvia: 'LV',
  Estonia: 'EE',
  Malta: 'MT',
  Cyprus: 'CY',
  Luxembourg: 'LU',
  Brazil: 'BR',
  Mexico: 'MX',
  Argentina: 'AR',
  Chile: 'CL',
  Colombia: 'CO',
  Peru: 'PE',
  Venezuela: 'VE',
  'South Africa': 'ZA',
  Egypt: 'EG',
  Nigeria: 'NG',
  Kenya: 'KE',
  Morocco: 'MA',
  'Saudi Arabia': 'SA',
  'United Arab Emirates': 'AE',
  Israel: 'IL',
  Turkey: 'TR',
  Russia: 'RU',
  Ukraine: 'UA',
  'South Korea': 'KR',
  Thailand: 'TH',
  Vietnam: 'VN',
  Malaysia: 'MY',
  Indonesia: 'ID',
  Philippines: 'PH',
  Pakistan: 'PK',
  Bangladesh: 'BD',
};

// Sorted list of countries for dropdown (Australia first, then alphabetical)
export const COUNTRIES_LIST = [
  'Australia',
  ...Object.keys(COUNTRY_CODE_MAP)
    .filter((country) => country !== 'Australia')
    .sort(),
];

// Reverse map: country code to country name
const CODE_TO_COUNTRY_MAP: Record<string, string> = Object.entries(
  COUNTRY_CODE_MAP
).reduce((acc, [country, code]) => {
  acc[code] = country;
  return acc;
}, {} as Record<string, string>);

export const mapCountryCode = (country: string): string => {
  if (!country) return 'AU'; // Default to Australia

  // Try exact match first
  if (COUNTRY_CODE_MAP[country]) {
    return COUNTRY_CODE_MAP[country];
  }

  // Try case-insensitive match
  const countryLower = country.toLowerCase();
  const matchedKey = Object.keys(COUNTRY_CODE_MAP).find(
    (key) => key.toLowerCase() === countryLower
  );

  if (matchedKey) {
    return COUNTRY_CODE_MAP[matchedKey];
  }

  // If no match found, return 'AU' as default
  return 'AU';
};

// Normalize country name from various formats (code, abbreviation, or full name)
export const normalizeCountryName = (countryInput: string): string => {
  if (!countryInput) return 'Australia'; // Default

  const input = countryInput.trim();

  // If it's already a valid country name in our list, return it
  if (COUNTRIES_LIST.includes(input)) {
    return input;
  }

  // Try to match by country code (e.g., "AU" → "Australia")
  const upperInput = input.toUpperCase();
  if (CODE_TO_COUNTRY_MAP[upperInput]) {
    return CODE_TO_COUNTRY_MAP[upperInput];
  }

  // Try case-insensitive match in COUNTRY_CODE_MAP
  const matchedKey = Object.keys(COUNTRY_CODE_MAP).find(
    (key) => key.toLowerCase() === input.toLowerCase()
  );
  if (matchedKey) {
    return matchedKey;
  }

  // Handle common abbreviations
  const abbreviationMap: Record<string, string> = {
    AUS: 'Australia',
    USA: 'United States',
    UK: 'United Kingdom',
    NZ: 'New Zealand',
    UAE: 'United Arab Emirates',
  };

  if (abbreviationMap[upperInput]) {
    return abbreviationMap[upperInput];
  }

  // Default to Australia if no match found
  return 'Australia';
};

export function isNonPaybleEntity(entity: Entity) {
  if (!entity.taxNumber) {
    return true;
  }

  if (!isVerifiedEntity(entity)) {
    return true;
  }

  return false;
}

export function needsVerificationCompletion(entity: Entity | null | undefined) {
  if (!entity) return false;

  return (
    entity.accountStatus === AccountStatus.CREATED ||
    entity.accountStatus === AccountStatus.ACTION_REQUIRED ||
    entity.accountStatus === AccountStatus.NOT_CREATED
  );
}

export function needsEntityVerification(entity: Entity | null | undefined) {
  if (!entity) return false;

  if (!entity.taxNumber) {
    return false;
  }

  return needsVerificationCompletion(entity);
}
